import { useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import { jobGuidManagerFactory } from '@shipstation/async-client-manager';
import { FreightBrowser } from '@shipstation/client-common/components/freight-browser';
import { getQuotes, saveQuoteToFulfillmentPlan } from '@shipstation/client-common/api/ltl.api';
import {
  mapBrowserServicesToAccessorials,
  mapDimensionUnit,
  mapHandlingUnitsToPayload,
  mapWeightUnit,
  mapWizardInsurance,
} from '@shipstation/client-common/ltl/freight-payload-mappers';
import { useFreightQuoteProgress } from '@shipstation/client-common/pusher/hooks/use-freight-quote-progress';
import { LTL_PUSHER_TIMEOUT_MS, makeLtlPusherService } from '@shipstation/client-common/pusher/ltl-fallback-helpers';
import { WIZARD_MODE } from '@shipstation/client-common/components/freight-booking-wizard';
import { Button } from 'ui-components/components/button';
import { NotificationService } from 'ui-components/services/notification-service';
import { PusherService } from '../../../../services/pusher-service';
import Global from '../../../../store/global-selectors';
import { getFreightGridRequest } from '../../../../store/ltl/ltl.actions';
import { FreightBookingWizardContainer } from '../freight-booking-wizard/freight-booking-wizard-container';
import { freightBrowserContainerMessages } from './messages';

import type { ReactNode } from 'react';

import type { ShipFrom } from '@shipstation/client-common/types/ship-from';
import type { BaseWeightUnit } from '@shipstation/client-common/types/weight';
import type { DimensionUnit } from '@shipstation/client-common/types/dimensions';

import type { FreightBookingFormState } from '@shipstation/client-common/components/freight-booking-wizard';
import type { FreightBrowserFormState } from '@shipstation/client-common/components/freight-browser';
import type { AsyncTaskErrorEnvelope } from '@shipstation/client-common/types/common';
import type { FreightQuoteRequest, FreightFulfillmentPlanSaveQuoteRequest } from '@shipstation/client-common/types/freight-requests';
import type { LtlAddress, LtlAdditionalServices, LtlHandlingUnit, LtlQuote } from '@shipstation/client-common/types/ltl';

export const DEFAULT_ADDITIONAL_SERVICES: LtlAdditionalServices = {
  isLiftgatePickup: false,
  isInsidePickup: false,
  isResidentialPickup: false,
  isLimitedAccessPickup: false,
  isFlatbedPickup: false,
  isDockLevelPickup: false,
  isTopLoadPickup: false,
  isConstructionSitePickup: false,
  isTradeShowPickup: false,
  isGroceryConsolidationPickup: false,
  isNotifyBeforePickup: false,
  isLiftgateDelivery: false,
  isInsideDelivery: false,
  isResidentialDelivery: false,
  isLimitedAccessDelivery: false,
  isFlatbedDelivery: false,
  isDockLevelDelivery: false,
  isConstructionSiteDelivery: false,
  isTradeShowDelivery: false,
  isGroceryConsolidationDelivery: false,
  isAppointmentDelivery: false,
  isCallBeforeDelivery: false,
  isSortAndSegregate: false,
  isNotifyBeforeDelivery: false,
  isHazmat: false,
  isTemperatureControl: false,
  isProtectFromFreezing: false,
  isProtectionFromHeat: false,
  isOverlength8to12ft: false,
  isOverlength12to20ft: false,
  isOverlength20ftPlus: false,
  isGuaranteedDelivery: false,
  isBlindShipment: false,
  isMarkAndTag: false,
  isCOD: false,
};

const EMPTY_ADDRESS: LtlAddress = {
  name: '',
  company: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  countryCode: 'US',
  phone: '',
  email: '',
  locationType: '',
};

// Builds the booking-wizard seed form-state from the browser's in-memory form. The
// browser only collects a subset of the wizard's fields, so unmapped wizard fields
// fall back to empty defaults. The wizard resolves the origin address itself from
// selectedShipFromId, so originAddress is left empty here.
const buildBookingFormState = (browserForm: FreightBrowserFormState, sellerFreightProviderId: number): FreightBookingFormState => {
  const services = browserForm.additionalServices;
  return {
    selectedShipFromId: browserForm.shipFromId,
    originAddress: EMPTY_ADDRESS,
    shipmentDate: '',
    pickupServices: {
      isInsidePickup: services.isInsidePickup,
      isConstructionSitePickup: services.isConstructionSitePickup,
      isLiftgatePickup: services.isLiftgatePickup,
      isNotifyBeforePickup: services.isNotifyBeforePickup,
      isResidentialPickup: services.isResidentialPickup,
      isDropAtTerminal: false,
      isGroceryConsolidationPickup: services.isGroceryConsolidationPickup,
      isLimitedAccess: services.isLimitedAccessPickup,
      isTradeshowPickup: services.isTradeShowPickup,
    },
    destinationAddress: {
      ...EMPTY_ADDRESS,
      city: browserForm.destination.city,
      state: browserForm.destination.state,
      postalCode: browserForm.destination.postalCode,
      countryCode: browserForm.destination.countryCode || 'US',
    },
    shouldSaveAsNewCustomer: false,
    deliveryServices: {
      isInsideDelivery: services.isInsideDelivery,
      isConstructionSiteDelivery: services.isConstructionSiteDelivery,
      isLiftgateDelivery: services.isLiftgateDelivery,
      isNotifyBeforeDelivery: services.isNotifyBeforeDelivery,
      isResidentialDelivery: services.isResidentialDelivery,
      isHoldAtTerminal: false,
      isGroceryConsolidationDelivery: services.isGroceryConsolidationDelivery,
      isLimitedAccess: services.isLimitedAccessDelivery,
      isTradeshowDelivery: services.isTradeShowDelivery,
    },
    tradeshow: { name: '', boothNumber: '' },
    tradeshowPickup: { name: '', boothNumber: '' },
    handlingUnits: browserForm.handlingUnits,
    shipmentServices: {
      isSortAndSegregate: services.isSortAndSegregate,
      isProtectFromFreeze: services.isProtectFromFreezing,
      isProtectionFromHeat: services.isProtectionFromHeat,
    },
    pickupReadyTime: '09:00',
    pickupCloseTime: '17:00',
    willArrangePickupSeparately: false,
    insurance: {
      isInsured: browserForm.insurance.isInsured,
      conditionOfGoods: browserForm.insurance.conditionOfGoods,
      category: browserForm.insurance.category,
      insuredValue: browserForm.insurance.insuredValue,
      marksAndNumbers: browserForm.insurance.marksAndNumbers,
    },
    handlingCharge: { isEnabled: false, value: 0 },
    references: { isEnabled: false, items: [] },
    pickupInstructions: '',
    deliveryInstructions: '',
    handlingInstructions: '',
    sellerFreightProviderID: sellerFreightProviderId,
  };
};

const INITIAL_FORM_STATE: Omit<FreightBrowserFormState, 'handlingUnits'> = {
  shipFromId: '',
  destination: { city: '', state: '', postalCode: '', countryCode: 'US' },
  additionalServices: DEFAULT_ADDITIONAL_SERVICES,
  insurance: { isInsured: false, conditionOfGoods: '', category: '', insuredValue: 0, marksAndNumbers: '' },
};

const mergeWithSeed = (defaults: FreightBrowserFormState, seed: FreightBrowserFormState): FreightBrowserFormState => ({
  ...defaults,
  ...seed,
  destination: { ...defaults.destination, ...seed.destination },
  additionalServices: { ...defaults.additionalServices, ...seed.additionalServices },
  insurance: { ...defaults.insurance, ...seed.insurance },
  handlingUnits: seed.handlingUnits?.length ? seed.handlingUnits : defaults.handlingUnits,
});

type FreightBrowserContainerProps = {
  isOpen: boolean;
  onClose: () => void;
  seedFormState?: FreightBrowserFormState;
};

export const FreightBrowserContainer = ({ isOpen, onClose, seedFormState }: FreightBrowserContainerProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const defaultShipFromId = useSelector(Global.selectSellerDefaultShipFromId);
  const shipFroms: ShipFrom[] = useSelector(Global.selectSellerShipFroms);
  const freightProviders = useSelector(Global.selectSellerFreightProviders);
  const weightUnit = useSelector(Global.selectSellerDefaultWeightUnit) as BaseWeightUnit;
  const dimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit) as DimensionUnit;
  const states = useSelector(Global.selectReferenceDatagetStates);

  const createDefaultHandlingUnitWithUnits = useCallback(
    (): LtlHandlingUnit => ({
      id: crypto.randomUUID(),
      packagingType: 'Pallet',
      quantity: 1,
      length: 0,
      width: 0,
      height: 0,
      dimensionUnit,
      isStackable: false,
      commodities: [
        {
          id: crypto.randomUUID(),
          description: '',
          freightClass: '',
          nmfcCode: '',
          quantity: 1,
          weight: 0,
          weightUnit,
          isHazmat: false,
          hazmatDetails: null,
          insurance: { isInsured: false, commodityCategoryCode: '', condition: '', declaredValue: 0 },
        },
      ],
    }),
    [dimensionUnit, weightUnit],
  );

  const [formState, setFormState] = useState<FreightBrowserFormState>(() => {
    const defaultFormState: FreightBrowserFormState = {
      ...INITIAL_FORM_STATE,
      handlingUnits: [createDefaultHandlingUnitWithUnits()],
      shipFromId: defaultShipFromId || '',
    };
    return seedFormState ? mergeWithSeed(defaultFormState, seedFormState) : defaultFormState;
  });
  const [rates, setRates] = useState<LtlQuote[]>([]);
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  const [rateError, setRateError] = useState<ReactNode>(undefined);
  const [rateErrorCode, setRateErrorCode] = useState<string | null | undefined>(undefined);
  // When set, the rate browser hands off to the booking wizard seeded with this data.
  const [configureSeed, setConfigureSeed] = useState<{
    formState: FreightBookingFormState;
    rates: LtlQuote[];
    selectedRate: LtlQuote | null;
  } | null>(null);

  const formStateRef = useRef(formState);
  formStateRef.current = formState;

  const pendingGuidsRef = useRef<Set<string>>(new Set());

  const pusherService = useMemo(
    () => makeLtlPusherService(pendingGuidsRef, PusherService.bindToEvent.bind(PusherService), jobGuidManagerFactory),
    [],
  );

  const { startListening, cancelListening } = useFreightQuoteProgress({
    pusherService,
    pendingGuidsRef,
    onQuotes: (quotes) => setRates((prev) => [...prev, ...quotes]),
    onError: (message, code) => {
      setRateError(message);
      setRateErrorCode(code ?? null);
      setIsLoadingRates(false);
    },
    onComplete: () => setIsLoadingRates(false),
    timeoutMs: LTL_PUSHER_TIMEOUT_MS,
  });

  const shipFromOptions = useMemo(() => {
    return shipFroms.map((sf) => ({ id: String(sf.shipFromId), name: sf.name }));
  }, [shipFroms]);

  const shipFromAddress = useMemo(() => {
    const matched = shipFroms.find((sf) => String(sf.shipFromId) === formState.shipFromId);
    return (
      matched?.originAddress ?? {
        name: '',
        company: null,
        phone: '',
        email: '',
        line1: '',
        line2: '',
        line3: null,
        city: '',
        state: '',
        postalCode: '',
        countryCode: 'US',
        residentialIndicator: 'Unknown' as const,
        ianaTimeZone: null,
      }
    );
  }, [shipFroms, formState.shipFromId]);

  const destinationAddress = useMemo(
    (): LtlAddress => ({
      name: '',
      company: '',
      line1: '',
      line2: '',
      city: formState.destination.city,
      state: formState.destination.state,
      postalCode: formState.destination.postalCode,
      countryCode: formState.destination.countryCode || 'US',
      phone: '',
      email: '',
      locationType: '',
    }),
    [formState.destination],
  );

  const getConnectedProviderId = useCallback((): number | undefined => {
    return freightProviders?.[0]?.sellerFreightProviderId ?? undefined;
  }, [freightProviders]);
  const connectedFreightProviderId = freightProviders?.[0]?.freightProviderId ?? null;

  const handleNavigateToCarrierSettings = useCallback(() => {
    navigate('/settings/carriers?tab=2');
    onClose();
  }, [navigate, onClose]);

  const handleBrowseRates = useCallback(async () => {
    const current = formStateRef.current;
    const sellerFreightProviderId = getConnectedProviderId();
    if (!sellerFreightProviderId) {
      setRateErrorCode(undefined);
      setRateError(
        <FormattedMessage
          {...freightBrowserContainerMessages.noProviderConnected}
          values={{
            link: (chunks) => (
              <Button onClick={handleNavigateToCarrierSettings} theme='link'>
                {chunks}
              </Button>
            ),
          }}
        />,
      );
      return;
    }

    if (!current.shipFromId) {
      setRateErrorCode(undefined);
      setRateError(intl.formatMessage(freightBrowserContainerMessages.noShipFromSelected));
      return;
    }

    setIsLoadingRates(true);
    setRateError(undefined);
    setRateErrorCode(undefined);
    setRates([]);
    cancelListening();

    const payload: FreightQuoteRequest = {
      sellerFreightProviderId,
      shipment: {
        shipFromId: Number(current.shipFromId),
        destinationAddress: {
          city: current.destination.city,
          state: current.destination.state,
          postalCode: current.destination.postalCode,
          countryCode: current.destination.countryCode,
        },
        shipmentDate: `${new Date().toISOString().split('T')[0]}T09:00:00`,
        handlingUnits: mapHandlingUnitsToPayload(current.handlingUnits, mapWeightUnit(weightUnit), mapDimensionUnit(dimensionUnit)),
        additionalServices: mapBrowserServicesToAccessorials(current.additionalServices),
        insurance: mapWizardInsurance(current.insurance),
      },
    };

    try {
      const data = (await getQuotes(payload)) as AsyncTaskErrorEnvelope;
      if (data.success === false) {
        setRateError(data.errors?.[0]?.message || intl.formatMessage(freightBrowserContainerMessages.failedToRetrieveQuotes));
        setIsLoadingRates(false);
        return;
      }
      const jobGuids: string[] = (data.jobGuids ?? data.JobGuids ?? []) as string[];
      if (jobGuids.length === 0) {
        setIsLoadingRates(false);
        return;
      }
      startListening(jobGuids);
    } catch (err) {
      const responseData = (err as { response?: { data?: unknown } })?.response?.data;
      const message =
        (typeof responseData === 'string' ? responseData : null) ??
        (responseData as { Message?: string })?.Message ??
        (err instanceof Error ? err.message : null) ??
        intl.formatMessage(freightBrowserContainerMessages.rateFetchError);
      setRateError(message);
      setIsLoadingRates(false);
    }
  }, [cancelListening, dimensionUnit, getConnectedProviderId, handleNavigateToCarrierSettings, intl, startListening, weightUnit]);

  const handleSaveQuote = useCallback(
    async (selectedQuote: LtlQuote | null) => {
      const current = formStateRef.current;
      const sellerFreightProviderId = getConnectedProviderId();
      if (!sellerFreightProviderId) return;

      const payload: FreightFulfillmentPlanSaveQuoteRequest = {
        fulfillmentPlanId: null,
        freightNumber: null,
        sellerFreightProviderId,
        productTransactionId: rates[0]?.productTransactionId,
        offerId: selectedQuote?.offerId ?? null,
        carrierName: selectedQuote?.carrierName ?? null,
        carrierScac: selectedQuote?.carrierScac ?? null,
        serviceName: selectedQuote?.serviceName ?? null,
        totalCharges: selectedQuote?.totalCharges ?? null,
        transitDays: selectedQuote?.transitDays ?? null,
        estimatedDeliveryDate: selectedQuote?.estimatedDeliveryDate ?? null,
        totalWeight: current.handlingUnits.reduce(
          (sum, unit) => sum + unit.commodities.reduce((cSum, c) => cSum + c.weight * c.quantity, 0) * unit.quantity,
          0,
        ),
        shipDate: new Date().toISOString().split('T')[0],
        shipFromWarehouseId: current.shipFromId ? Number(current.shipFromId) : null,
        destinationAddress: {
          city: current.destination.city,
          state: current.destination.state,
          postalCode: current.destination.postalCode,
          countryCode: current.destination.countryCode,
        },
        handlingUnits: mapHandlingUnitsToPayload(current.handlingUnits, mapWeightUnit(weightUnit), mapDimensionUnit(dimensionUnit)),
        additionalServices: mapBrowserServicesToAccessorials(current.additionalServices),
        insurance: mapWizardInsurance(current.insurance),
        allQuotes: rates.length
          ? rates.map((q) => ({
              offerId: q.offerId,
              productTransactionId: q.productTransactionId,
              carrierName: q.carrierName,
              carrierScac: q.carrierScac,
              serviceName: q.serviceName,
              totalCharges: q.totalCharges,
              transitDays: q.transitDays,
              estimatedDeliveryDate: q.estimatedDeliveryDate,
              isGuaranteed: q.isGuaranteed,
              serviceType: q.serviceType ?? null,
              originTerminalCode: q.originTerminalCode ?? null,
              destinationTerminalCode: q.destinationTerminalCode ?? null,
              insuredAmount: q.insuredAmount ?? null,
              insurancePremium: q.insurancePremium ?? null,
              insuranceCertificateNumber: q.insuranceCertificateNumber ?? null,
            }))
          : null,
      };

      try {
        await saveQuoteToFulfillmentPlan(payload);
        NotificationService.showGenericSuccessNotification(
          undefined,
          <FormattedMessage {...freightBrowserContainerMessages.saveQuoteSuccess} />,
        );
        dispatch(getFreightGridRequest());
      } catch (err) {
        const errorMessage =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          intl.formatMessage(freightBrowserContainerMessages.saveQuoteFailed);
        NotificationService.showGenericFailureNotification(undefined, errorMessage);
      }
    },
    [dimensionUnit, dispatch, getConnectedProviderId, intl, rates, weightUnit],
  );

  const handleConfigureShipment = useCallback(
    (selectedQuote: LtlQuote) => {
      const current = formStateRef.current;
      // Hand off the in-memory browser form + fetched rates to the booking wizard.
      // The wizard resolves the origin address itself from selectedShipFromId.
      setConfigureSeed({
        formState: buildBookingFormState(current, getConnectedProviderId()!),
        rates,
        selectedRate: selectedQuote,
      });
    },
    [getConnectedProviderId, rates],
  );

  const handleCloseWizard = useCallback(() => {
    setConfigureSeed(null);
    onClose();
  }, [onClose]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  if (configureSeed) {
    return (
      <FreightBookingWizardContainer
        isOpen
        mode={WIZARD_MODE.NEW}
        onClose={handleCloseWizard}
        seedFormState={configureSeed.formState}
        seedRates={configureSeed.rates}
        seedSelectedRate={configureSeed.selectedRate}
      />
    );
  }

  return (
    <FreightBrowser
      connectedFreightProviderId={connectedFreightProviderId}
      destinationAddress={destinationAddress}
      formState={formState}
      isLoadingRates={isLoadingRates}
      isOpen={isOpen}
      onBrowseRates={handleBrowseRates}
      onClose={handleClose}
      onConfigureShipment={handleConfigureShipment}
      onFormChange={setFormState}
      onSaveQuote={handleSaveQuote}
      rateError={rateError}
      rateErrorCode={rateErrorCode}
      rates={rates}
      shipFromAddress={shipFromAddress}
      shipFromOptions={shipFromOptions}
      states={states}
    />
  );
};
